"use client";

import { useState } from "react";
import { ArrowRight, ArrowLeft, RotateCcw, Sparkles } from "lucide-react";
import { LIFE_PATH_ARCHETYPES } from "@/lib/numerology/archetype-data";

// ── Quiz data ─────────────────────────────────────────────────────────────────

interface Answer {
  text: string;
  scores: number[]; // life paths that score +1 for this answer
}
interface Question {
  id: number;
  question: string;
  emoji: string;
  answers: Answer[];
}

const QUESTIONS: Question[] = [
  {
    id: 1,
    emoji: "🎯",
    question: "¿Cómo tomas las decisiones importantes?",
    answers: [
      { text: "Al instante, guiándome por la intuición y el fuego interior", scores: [1, 5] },
      { text: "Analizo todo con cuidado: hechos, riesgos, opciones", scores: [4, 7] },
      { text: "Consulto con personas cuya opinión valoro", scores: [2, 6] },
      { text: "Espero una señal o el momento en que «llega solo»", scores: [7, 9, 11] },
    ],
  },
  {
    id: 2,
    emoji: "⚡",
    question: "¿Qué es lo que más te llena de energía?",
    answers: [
      { text: "Nuevos proyectos, lanzamientos, primeros pasos", scores: [1, 3] },
      { text: "Conversaciones profundas y relaciones intensas", scores: [2, 6] },
      { text: "Lograr resultados concretos y el reconocimiento", scores: [8, 4] },
      { text: "El conocimiento: libros, viajes, descubrimientos", scores: [7, 5, 9] },
    ],
  },
  {
    id: 3,
    emoji: "🌟",
    question: "¿Qué cumplido te conmueve más?",
    answers: [
      { text: "«Eres tan inspirador y lleno de energía»", scores: [1, 3] },
      { text: "«Siempre se puede contar contigo»", scores: [4, 6, 2] },
      { text: "«Ves lo que los demás no notan»", scores: [7, 11, 9] },
      { text: "«Cambias el mundo a tu alrededor»", scores: [8, 22, 9] },
    ],
  },
  {
    id: 4,
    emoji: "😤",
    question: "¿Qué es lo que más te irrita?",
    answers: [
      { text: "La lentitud y el miedo al cambio en los demás", scores: [1, 5] },
      { text: "La mentira, la injusticia y la traición", scores: [2, 9, 6] },
      { text: "El caos, el desorden, la incoherencia", scores: [4, 8, 7] },
      { text: "Las limitaciones: reglas por el simple hecho de tenerlas", scores: [5, 3, 11] },
    ],
  },
  {
    id: 5,
    emoji: "🌈",
    question: "¿Qué escenario de vida ideal sientes más cercano?",
    answers: [
      { text: "Construyo algo propio: una empresa, un producto, un legado", scores: [1, 8, 4] },
      { text: "Estoy rodeado de personas que me quieren en un hogar acogedor", scores: [6, 2, 9] },
      { text: "Viajo, aprendo, exploro sin límites", scores: [5, 7, 3] },
      { text: "Ayudo a muchas personas a cambiar su vida", scores: [9, 6, 11, 22] },
    ],
  },
  {
    id: 6,
    emoji: "🤝",
    question: "¿Cómo te describen tus seres queridos?",
    answers: [
      { text: "«Eres nuestro líder, vamos contigo»", scores: [1, 8] },
      { text: "«Contigo siempre hay calidez y calma»", scores: [6, 2, 9] },
      { text: "«Nunca eres aburrido: siempre se te ocurre algo»", scores: [3, 5, 11] },
      { text: "«Sabes percibir lo que los demás pasan por alto»", scores: [7, 4, 9] },
    ],
  },
  {
    id: 7,
    emoji: "🔄",
    question: "¿Cuál es tu actitud ante los cambios?",
    answers: [
      { text: "Yo los provoco: estoy al inicio de cada uno", scores: [1, 5] },
      { text: "Me adapto rápido, el cambio es mi elemento", scores: [5, 3, 7] },
      { text: "Los cambios asustan, pero los afronto si hace falta", scores: [4, 6, 2] },
      { text: "El cambio es parte natural de la vida, todo a su tiempo", scores: [9, 7, 11, 22] },
    ],
  },
];

const COLOR_MAP: Record<number, { bg: string; border: string; text: string; badge: string; dot: string }> = {
  1:  { bg: "bg-rose-50",    border: "border-rose-200",    text: "text-rose-700",    badge: "bg-rose-100 text-rose-700",    dot: "bg-rose-500" },
  2:  { bg: "bg-sky-50",     border: "border-sky-200",     text: "text-sky-700",     badge: "bg-sky-100 text-sky-700",      dot: "bg-sky-500" },
  3:  { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-700",   badge: "bg-amber-100 text-amber-700",  dot: "bg-amber-500" },
  4:  { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", badge: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  5:  { bg: "bg-violet-50",  border: "border-violet-200",  text: "text-violet-700",  badge: "bg-violet-100 text-violet-700", dot: "bg-violet-500" },
  6:  { bg: "bg-pink-50",    border: "border-pink-200",    text: "text-pink-700",    badge: "bg-pink-100 text-pink-700",    dot: "bg-pink-500" },
  7:  { bg: "bg-indigo-50",  border: "border-indigo-200",  text: "text-indigo-700",  badge: "bg-indigo-100 text-indigo-700", dot: "bg-indigo-500" },
  8:  { bg: "bg-orange-50",  border: "border-orange-200",  text: "text-orange-700",  badge: "bg-orange-100 text-orange-700", dot: "bg-orange-500" },
  9:  { bg: "bg-slate-50",   border: "border-slate-200",   text: "text-slate-700",   badge: "bg-slate-100 text-slate-700",  dot: "bg-slate-500" },
  11: { bg: "bg-violet-50",  border: "border-violet-300",  text: "text-violet-800",  badge: "bg-violet-100 text-violet-800", dot: "bg-violet-600" },
  22: { bg: "bg-purple-50",  border: "border-purple-200",  text: "text-purple-700",  badge: "bg-purple-100 text-purple-700", dot: "bg-purple-500" },
};

// ── Scoring ───────────────────────────────────────────────────────────────────

function calcResult(selected: number[]): { top: number; second: number; scores: Record<number, number> } {
  const scores: Record<number, number> = {};
  for (let n = 1; n <= 9; n++) scores[n] = 0;

  QUESTIONS.forEach((q, i) => {
    const ansIdx = selected[i];
    if (ansIdx === undefined) return;
    q.answers[ansIdx].scores.forEach(lp => {
      const key = lp > 9 ? lp : lp; // include 11/22 directly
      scores[key] = (scores[key] ?? 0) + 1;
    });
  });

  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  return {
    top: Number(sorted[0]?.[0] ?? 1),
    second: Number(sorted[1]?.[0] ?? 2),
    scores,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  lifePath: number | null;
}

export function NumerologyQuiz({ lifePath }: Props) {
  const [step, setStep] = useState<"intro" | "quiz" | "result">("intro");
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState<number[]>([]);
  const [hoveredAnswer, setHoveredAnswer] = useState<number | null>(null);

  const totalQuestions = QUESTIONS.length;
  const q = QUESTIONS[current];

  function pickAnswer(idx: number) {
    const newSelected = [...selected];
    newSelected[current] = idx;
    setSelected(newSelected);

    // Auto-advance after brief delay
    setTimeout(() => {
      if (current < totalQuestions - 1) {
        setCurrent(current + 1);
        setHoveredAnswer(null);
      } else {
        setStep("result");
      }
    }, 300);
  }

  function goBack() {
    if (current > 0) setCurrent(current - 1);
    else setStep("intro");
  }

  function reset() {
    setStep("intro");
    setCurrent(0);
    setSelected([]);
    setHoveredAnswer(null);
  }

  if (step === "intro") {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-violet-700 to-indigo-800 rounded-2xl p-7 text-white text-center">
          <div className="text-5xl mb-4">🔮</div>
          <h2 className="text-xl font-bold mb-2">Tu arquetipo numerológico</h2>
          <p className="text-violet-200 text-sm leading-relaxed max-w-sm mx-auto">
            7 preguntas sobre tu naturaleza y descubrirás qué arquetipo vive dentro de ti. ¿Coincidirá el resultado con tu número del camino de vida?
          </p>
          {lifePath && (
            <p className="text-violet-300 text-xs mt-3">
              Tu camino de vida: <strong className="text-white">{lifePath}</strong> — comprueba la coincidencia
            </p>
          )}
        </div>

        <div className="grid grid-cols-3 gap-3 text-center text-sm">
          {[{ emoji: "⏱️", label: "3 minutos" }, { emoji: "❓", label: "7 preguntas" }, { emoji: "✨", label: "Resultado personal" }].map(item => (
            <div key={item.label} className="bg-white border border-stone-200 rounded-xl py-3 px-2">
              <div className="text-xl mb-1">{item.emoji}</div>
              <div className="text-xs text-stone-600 font-medium">{item.label}</div>
            </div>
          ))}
        </div>

        <button
          onClick={() => setStep("quiz")}
          className="w-full flex items-center justify-center gap-2 h-12 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-xl transition-colors"
        >
          Empezar el test
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (step === "quiz") {
    const progress = ((current) / totalQuestions) * 100;
    return (
      <div className="space-y-5">
        {/* Progress */}
        <div>
          <div className="flex items-center justify-between text-xs text-stone-500 mb-2">
            <span>Pregunta {current + 1} de {totalQuestions}</span>
            <button onClick={goBack} className="flex items-center gap-1 text-stone-400 hover:text-stone-600 transition-colors">
              <ArrowLeft className="w-3 h-3" /> Atrás
            </button>
          </div>
          <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-violet-500 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question */}
        <div className="bg-white border border-stone-200 rounded-2xl p-5">
          <div className="text-3xl mb-3 text-center">{q.emoji}</div>
          <h3 className="text-base font-bold text-stone-800 text-center mb-5">{q.question}</h3>
          <div className="space-y-2.5">
            {q.answers.map((a, idx) => {
              const wasSelected = selected[current] === idx;
              return (
                <button
                  key={idx}
                  onClick={() => pickAnswer(idx)}
                  onMouseEnter={() => setHoveredAnswer(idx)}
                  onMouseLeave={() => setHoveredAnswer(null)}
                  className={`w-full text-left px-4 py-3.5 rounded-xl border text-sm font-medium transition-all ${
                    wasSelected
                      ? "bg-violet-600 border-violet-600 text-white shadow-sm"
                      : hoveredAnswer === idx
                      ? "bg-violet-50 border-violet-300 text-violet-800"
                      : "bg-stone-50 border-stone-200 text-stone-700 hover:border-stone-300"
                  }`}
                >
                  <span className="mr-2 text-stone-400 font-normal">{String.fromCharCode(65 + idx)}.</span>
                  {a.text}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Result
  const result = calcResult(selected);
  const topArchetype = LIFE_PATH_ARCHETYPES[result.top] ?? LIFE_PATH_ARCHETYPES[1];
  const secondArchetype = LIFE_PATH_ARCHETYPES[result.second] ?? LIFE_PATH_ARCHETYPES[2];
  const colors = COLOR_MAP[result.top] ?? COLOR_MAP[1];
  const matches = lifePath !== null && lifePath === result.top;

  return (
    <div className="space-y-5">
      {/* Result hero */}
      <div className={`rounded-2xl border-2 p-6 text-center space-y-3 ${colors.bg} ${colors.border}`}>
        <div className="text-5xl">{topArchetype.emoji}</div>
        <div>
          <span className={`text-[10px] font-bold uppercase tracking-widest ${colors.text}`}>Tu arquetipo</span>
          <h2 className="text-2xl font-black text-stone-800 mt-1">{topArchetype.name}</h2>
          <p className={`text-sm font-semibold mt-1 ${colors.text}`}>{topArchetype.tagline}</p>
        </div>
        <p className="text-sm text-stone-600 leading-relaxed max-w-sm mx-auto">{topArchetype.essence}</p>

        {/* Life path match */}
        {lifePath !== null && (
          <div className={`inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full ${
            matches ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-600"
          }`}>
            {matches ? "✅" : "🔄"}
            {matches
              ? `¡Coincide con tu camino de vida ${lifePath}!`
              : `Tu camino de vida ${lifePath} — ${LIFE_PATH_ARCHETYPES[lifePath]?.name ?? "desconocido"}`
            }
          </div>
        )}
      </div>

      {/* Gifts */}
      <div className="bg-white border border-stone-200 rounded-2xl p-5 space-y-3">
        <p className="text-xs font-bold text-stone-400 uppercase tracking-widest">Tus dones naturales</p>
        <div className="flex flex-wrap gap-2">
          {topArchetype.gifts.map(g => (
            <span key={g} className={`text-xs font-medium px-2.5 py-1 rounded-full border ${colors.bg} ${colors.border} ${colors.text}`}>{g}</span>
          ))}
        </div>
        <div className="flex items-start gap-2 bg-stone-50 border border-stone-100 rounded-xl px-3 py-2.5">
          <span className="text-sm shrink-0">🌑</span>
          <p className="text-xs text-stone-600 leading-relaxed">{topArchetype.shadow}</p>
        </div>
      </div>

      {/* Mission */}
      <div className={`rounded-2xl border p-4 ${colors.bg} ${colors.border}`}>
        <p className="text-[10px] font-bold uppercase tracking-widest mb-2 ${colors.text}">Misión de vida</p>
        <p className="text-sm text-stone-700 leading-relaxed">{topArchetype.lifeMission}</p>
      </div>

      {/* Secondary archetype */}
      <div className="bg-white border border-stone-200 rounded-2xl p-4 flex items-start gap-3">
        <span className="text-2xl shrink-0">{secondArchetype.emoji}</span>
        <div>
          <p className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-1">Arquetipo secundario</p>
          <p className="text-sm font-bold text-stone-800">{secondArchetype.name}</p>
          <p className="text-xs text-stone-500 mt-0.5 leading-relaxed">{secondArchetype.tagline}</p>
        </div>
      </div>

      {/* Mismatch explanation */}
      {lifePath !== null && !matches && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-amber-500 shrink-0" />
            <p className="text-xs font-bold text-amber-800">Tu arquetipo y tu camino de vida difieren</p>
          </div>
          <p className="text-xs text-amber-700 leading-relaxed">
            ¡Es normal y muy interesante! El resultado del test muestra tus preferencias conscientes,
            mientras que el número del camino de vida ({lifePath} — {LIFE_PATH_ARCHETYPES[lifePath]?.name}) refleja tu naturaleza profunda.
            Pueden divergir: a veces nos atraen cualidades que queremos desarrollar en nosotros mismos.
          </p>
        </div>
      )}

      {/* Retry */}
      <button
        onClick={reset}
        className="w-full flex items-center justify-center gap-2 h-11 border border-stone-200 text-stone-600 text-sm font-medium rounded-xl hover:bg-stone-50 transition-colors"
      >
        <RotateCcw className="w-4 h-4" />
        Repetir el test
      </button>
    </div>
  );
}
